import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Shield } from 'lucide-react';
import ChangePasswordDialog from './ChangePasswordDialog';
import OnboardingWizard from './OnboardingWizard';

// Detecta se o token actual é uma sessão de impersonation (super-admin ou hub).
// Nesses casos, saltamos o modal de "must_change_password" — o agente de suporte
// não deve alterar a password do cliente.
function _decodeImpersonated(token) {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !!payload.impersonated_by;
  } catch {
    return false;
  }
}

export default function ProtectedRoute({ children, requireRole }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg">
              <Shield className="w-7 h-7 text-white" />
            </div>
          </div>
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900">A carregar...</h2>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role gate — redirects to /dashboard if user lacks required role
  if (requireRole) {
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!allowed.includes(user?.role)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Forced password rotation (first login / admin reset).
  // Excepção: durante impersonation (Hub ou Super Admin) NÃO forçamos mudança,
  // porque o agente de suporte não deve alterar credenciais do cliente.
  const isImpersonation = _decodeImpersonated(localStorage.getItem('auth_token'));
  const mustChange = !isImpersonation && !!user?.must_change_password;

  return (
    <>
      {children}
      {mustChange && (
        <ChangePasswordDialog
          open={true}
          onOpenChange={() => { /* forced — cannot close */ }}
          forced
        />
      )}
      {!mustChange && !isImpersonation && <OnboardingWizard />}
    </>
  );
}
